import {useCallback, useMemo} from 'react'
import {useWatch} from 'react-hook-form'

import {useFieldConfigurationContext} from '../../configuration/useConfigurationContext'
import {useOrderProcessFormContext} from '../../form/useOrderProcessFormContext'

import {getDefaultPumpSettings} from './pumps.utils'
import {PumpForm} from './types'
import {OWN_CONTRACTED_PUMP} from './useUnloadingMethods'

/**
 * Hook for managing pump configuration and settings
 */
export const usePumps = (availableUnloadingMethods: string[]) => {
  const {control, setValue, trigger, formState: {defaultValues}} = useOrderProcessFormContext()
  const pumpConfiguration = useFieldConfigurationContext('pumps')

  const availablePumpTypes = useMemo(() => {
    return pumpConfiguration.isEnabled
      ? pumpConfiguration.pumpTypes.values.map((pumpType) => ({
          ...pumpType,
          sizeUom: pumpConfiguration.pumpTypes.sizeUom
        }))
      : []
  }, [pumpConfiguration])

  const pump = useWatch({name: 'unloadingMethod.pump', control})
  const defaultPump = defaultValues?.unloadingMethod?.pump

  const selectedPump = useMemo(() => {
    if (!pumpConfiguration.isEnabled || !pump) {
      return undefined
    }

    const pumpType = pumpConfiguration.pumpTypes.values.find((x) => x.number === pump?.pumpNumber)

    return {
      pumpType,
      pumpSettings: pump?.pumpSettings
    }
  }, [pump, pumpConfiguration])

  const defaultPumpSettings = useMemo(
    () => getDefaultPumpSettings(pumpConfiguration),
    [pumpConfiguration]
  )

  const clearPump = useCallback(() => {
    setValue('unloadingMethod.pump', undefined)
    setValue('unloadingMethod.unloadingMethodCode', availableUnloadingMethods[0])
    void trigger()
  }, [setValue, trigger, availableUnloadingMethods])

  const setPumpType = useCallback(
    (pumpNumber?: string) => {
      if (!pumpNumber) {
        clearPump()
        return
      }

      if (!pumpConfiguration.isEnabled || !defaultPumpSettings) {
        console.warn(
          '[usePumps] setPumpType: Pump configuration is disabled or default settings are missing.',
          {
            attemptedPumpNumber: pumpNumber
          }
        )
        clearPump()
        return
      }

      const pumpType = pumpConfiguration.pumpTypes.values.find((x) => x.number === pumpNumber)
      if (!pumpType) {
        console.warn(
          `[usePumps] setPumpType: Pump type "${pumpNumber}" not found in available pump types.`,
          {
            attemptedPumpNumber: pumpNumber,
            availablePumps: pumpConfiguration.pumpTypes
          }
        )
        clearPump()
        return
      }

      setValue('unloadingMethod.pump', {
        pumpNumber,
        pumpSettings: selectedPump?.pumpSettings ?? defaultPumpSettings
      })
      setValue('unloadingMethod.unloadingMethodCode', OWN_CONTRACTED_PUMP)
      void trigger()
    },
    [
      clearPump,
      defaultPumpSettings,
      pumpConfiguration,
      selectedPump?.pumpSettings,
      setValue,
      trigger
    ]
  )

  const setPumpSettings = useCallback(
    (pumpSettingsOverrides: Partial<Omit<PumpForm['pumpSettings'], 'discharge'>>) => {
      if (!pumpConfiguration.isEnabled) {
        console.warn('[usePumps] setPumpSettings: Pump configuration is disabled.', {
          attemptedSettings: pumpSettingsOverrides
        })
        return
      }

      const newSettings = {
        ...pump?.pumpSettings,
        ...pumpSettingsOverrides
      } as PumpForm['pumpSettings']

      setValue('unloadingMethod.pump.pumpSettings', newSettings)
      void trigger()
    },
    [pump?.pumpSettings, pumpConfiguration, setValue, trigger]
  )

  const setPumpDischarge = useCallback(
    (discharge: {method: 'boom'} | {method: 'hose'; hoseLength?: number}) => {
      if (!pumpConfiguration.isEnabled) {
        console.warn('[usePumps] setPumpDischarge: Pump configuration is disabled.', {
          attemptedDischarge: discharge
        })
        return
      }

      const finalDischarge =
        discharge.method === 'hose'
          ? {
              method: 'hose' as const,
              hoseLength:
                discharge.hoseLength ??
                pumpConfiguration.hoseLength.default ??
                pumpConfiguration.hoseLength.min
            }
          : discharge

      setValue('unloadingMethod.pump.pumpSettings.discharge', finalDischarge)
      void trigger()
    },
    [pumpConfiguration, setValue, trigger]
  )

  return {
    selectedPump,
    defaultPump,
    availablePumpTypes,
    setPumpType,
    setPumpSettings,
    setPumpDischarge,
    configuration: pumpConfiguration
  }
}
